import { InteractionType, EventType } from '@azure/msal-browser';
import { b2cPolicies } from '../../auth/authConfig';
import { logError } from '../utilities/AppInsightsUtil';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

const getRefreshToken = async (instance, accounts) => {
  let response;
  if (accounts && accounts.length && instance) {
    try {
      response = await  instance.acquireTokenSilent({
          account:  accounts[0],
      });
    } catch (error) {
      if (error.name === 'InteractionRequiredAuthError') {
        logError(`Error Event: Token Error - Error Description: InteractionRequiredAuthError - ${error}`, SeverityLevel.Error, 'getRefreshToken');
        return null;
      }
    }
    return { token: response.idToken, idTokenAccount: response.account }
  }
};

// Handles success events related to login or token acquisition
function handleSuccessEvent(instance, event) {
  if (event.payload.account && event.payload.idTokenClaims['acr'].toLowerCase() === b2cPolicies.names.editUserId.toLowerCase()) {
    const originalSignInAccount = instance.getAllAccounts().find(account =>
      account.idTokenClaims.oid === event.payload.idTokenClaims.oid &&
      account.idTokenClaims.sub === event.payload.idTokenClaims.sub &&
      account.idTokenClaims['acr'].toLowerCase() === b2cPolicies.names.signIn.toLowerCase()
    );

    let signInFlowRequest = {
      authority: b2cPolicies.authorities.signIn.authority,
      account: originalSignInAccount
    };

    try {
      instance.ssoSilent(signInFlowRequest);
    } catch (error) {
      if (error.name === 'InteractionRequiredAuthError') {
        logError(`Error Event: Token Error - Error Description: InteractionRequiredAuthError - ${error}`, SeverityLevel.Error, 'handleSuccessEvent');
        return null;
      }
    }
  }
}

// Handles failures during SSO or token acquisition
function handleFailureEvent(instance, event, loginRequest) {
  if ((event.eventType === EventType.SSO_SILENT_FAILURE && event.error?.errorCode === 'monitor_window_timeout') ||
      (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE && event.interactionType === InteractionType.Silent)) {
        logError(`Error Event: Token Error - Error Description: ${event.eventType} - ${event.error}`, SeverityLevel.Error, 'handleFailureEvent');
        return null;
    }
}

// Main function to set up event callbacks and provide cleanup logic
const handleAuthSuccessFail = (instance, loginRequest) => {
  const successCallbackId = instance.addEventCallback(event => {
    if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
      handleSuccessEvent(instance, event);
    }
  });

  const failureCallbackId = instance.addEventCallback(event => handleFailureEvent(instance, event, loginRequest));

  // Cleanup function
  return () => {
    [successCallbackId, failureCallbackId].forEach(id => {
      if(id) { 
        instance.removeEventCallback(id) 
      };
    });
  };
};

const dispatchCurrentAuth = async (dispatch, accounts, setFirstEntry, setEntryFromFederation, fromFederation, setFromFederation, fromSignup, setFromSignup, setAgencyId) => {
  let acr = '';
  if (accounts[0].tenantId !== undefined) {
    acr = accounts[0].tenantId;
  }
  
  if(acr.includes('amigaggwsignin')) {
    await dispatch(setAgencyId(null));
  }

  if ((acr.includes('amigagsignup')) && fromFederation !== true) {
    setFirstEntry(true);
    dispatch(setFromSignup(true));
    await dispatch(setAgencyId(null));
  } else if ((acr.includes('amigagfederatedaccounts')) && fromSignup !== true) {
    setEntryFromFederation(true);
    dispatch(setFromFederation(true));
  } else if (fromFederation !== true && fromSignup !== true) {
    setFirstEntry(false);
    setEntryFromFederation(false);
    dispatch(setFromFederation(false));
    dispatch(setFromSignup(false));
    await dispatch(setAgencyId(null));
  }
};

const setAgencyAccountId = async (dispatch, accounts, setAgencyId, ssoAgent) => {
  const agency_id = ssoAgent ? ssoAgent : accounts[0].idTokenClaims.agency_id;
  if(agency_id) {
    dispatch(setAgencyId(agency_id));
  }
  const oid = accounts[0].idTokenClaims.oid;
  if (oid !== undefined) {
      window.postMessage(['adobeCiamUpdate', oid], '*');
  }
};

const checkTokenExpiry  = (instance, accounts) => {
  let currentTime = null;
  let expirationDate = null;
  const account = instance.getActiveAccount?.() || accounts?.[0] || null;
  if(account) {
      let tokenExpiration = account.idTokenClaims.exp;
      expirationDate = new Date(tokenExpiration * 1000);
      const fiveMinutesBeforeExpiration = new Date(expirationDate.getTime() - 5 * 60 * 1000);
      currentTime = new Date();
      if (currentTime >= fiveMinutesBeforeExpiration && currentTime < expirationDate) {
        return true;
      }
  }

  if(!account || currentTime > expirationDate) {
      instance.logout();
  }

  return null;
};

export { getRefreshToken, handleAuthSuccessFail, dispatchCurrentAuth, setAgencyAccountId, checkTokenExpiry};
